import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthProvider";
import { jwtDecode } from "jwt-decode";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Typography,
    Box,
    IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getClientTaskDetails } from "../../../services/reports.service";
import dayjs from "dayjs";

import { ISSUETYPE } from "../../../common/constant/constant";
import { isAdminRole } from "../../../utils/roles";

const ClientTaskDetailsDialog = ({ open, handleClose, data }) => {
    const [loading, setLoading] = useState(false);
    const [taskDetails, setTaskDetails] = useState([]);
    const [totalHours, setTotalHours] = useState("0:00");
    const [isAdmin, setIsAdmin] = useState(false);
    const navigate = useNavigate();
    const { accessToken } = useAuth();

    const issueTypeLabels = {
        [ISSUETYPE.BUG]: "Bug",
        [ISSUETYPE.TASK]: "Task",
        [ISSUETYPE.TESTING]: "Testing",
        [ISSUETYPE.DESIGNING]: "Designing",
        [ISSUETYPE.OTHERS]: "Others",
        [ISSUETYPE.MEETING]: "Meeting",
        [ISSUETYPE.DEPLOYMENT]: "Deployment",
    };

    useEffect(() => {
        if (accessToken) {
            try {
                const decoded = jwtDecode(accessToken);
                const role = decoded.role;
                setIsAdmin(isAdminRole(role) || role === 5);
            } catch (error) {
                console.error("Invalid token", error);
            }
        }
    }, [accessToken]);

    useEffect(() => {
        if (open && data?.projectId && (data?.date || (data?.startDate && data?.endDate))) {
            fetchTaskDetails();
        } else {
            setTaskDetails([]);
            setTotalHours("0:00");
        }
    }, [open, data]);

    const fetchTaskDetails = async () => {
        setLoading(true);
        try {
            const response = await getClientTaskDetails({
                projectId: data.projectId,
                date: data.date,
                startDate: data.startDate,
                endDate: data.endDate,
                userId: data.userId,
                issueType: data.issueType,
            });
            if (response?.data) {
                setTaskDetails(response.data);
                calculateTotalHours(response.data);
            }
        } catch (error) {
            console.error("Error fetching task details:", error);
        } finally {
            setLoading(false);
        }
    };

    const calculateTotalHours = (tasks) => {
        let totalMinutes = 0;
        tasks.forEach(task => {
            if (task.minutes !== undefined && task.hours !== undefined) {
                totalMinutes += (task.hours * 60) + task.minutes;
            }
        });
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        setTotalHours(`${hours}:${minutes.toString().padStart(2, '0')}`);
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return "-";
        return dayjs(timestamp).format("h:mm A");
    };

    // Format date subtitle based on whether it's a single date or date range
    const getDateSubtitle = () => {
        if (data?.date) {
            return dayjs(data.date).format("DD-MMM-YYYY");
        } else if (data?.startDate && data?.endDate) {
            return `${dayjs(data.startDate).format("DD-MMM-YYYY")} to ${dayjs(data.endDate).format("DD-MMM-YYYY")}`;
        }
        return "";
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: { borderRadius: 12 }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f5f5f5' }}>
                <Box>
                    <Typography variant="h6" component="div">
                        {data?.projectName || "Task Details"}
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary">
                        {getDateSubtitle()}
                    </Typography>
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{ color: (theme) => theme.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} elevation={0} variant="outlined">
                        <Table sx={{ minWidth: 650 }} aria-label="task details table">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#fafafa' }}>
                                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Task No</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Task Title</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>Description</TableCell>
                                    {isAdmin && <TableCell align="left" sx={{ fontWeight: 'bold', py: 2 }}>Assigned To</TableCell>}
                                    <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>Start Time</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>End Time</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', py: 2 }}>Duration</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.values(taskDetails.reduce((acc, item) => {
                                    if (!acc[item.taskNo]) {
                                        acc[item.taskNo] = { ...item, logs: [] };
                                    }
                                    acc[item.taskNo].logs.push(item);
                                    return acc;
                                }, {})).map((task, taskIndex) => (
                                    task.logs.map((log, logIndex) => (
                                        <TableRow
                                            key={`${taskIndex}-${logIndex}`}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            {logIndex === 0 && (
                                                <>
                                                    <TableCell
                                                        rowSpan={task.logs.length}
                                                        sx={{
                                                            py: 1.5,
                                                            verticalAlign: 'middle',
                                                            cursor: isAdmin ? 'pointer' : 'default',
                                                            color: isAdmin ? 'primary.main' : 'inherit',
                                                            textDecoration: isAdmin ? 'none' : 'none',
                                                            '&:hover': {
                                                                color: isAdmin ? 'primary.dark' : 'inherit'
                                                            }
                                                        }}
                                                        onClick={() => {
                                                            if (isAdmin && task.taskId) {
                                                                navigate(`/home/taskDetails?taskId=${task.taskId}`);
                                                            }
                                                        }}
                                                    >
                                                        {task.taskNo}
                                                    </TableCell>
                                                    <TableCell
                                                        rowSpan={task.logs.length}
                                                        sx={{ py: 1.5, verticalAlign: 'middle' }}
                                                    >
                                                        {task.taskTitle}
                                                    </TableCell>
                                                    <TableCell
                                                        rowSpan={task.logs.length}
                                                        align="center"
                                                        sx={{ py: 1.5, verticalAlign: 'middle' }}
                                                    >
                                                        <div
                                                            dangerouslySetInnerHTML={{ __html: task.description }}
                                                            style={{ maxWidth: '300px', whiteSpace: 'normal', wordBreak: 'break-word', fontSize: '14px', margin: 'auto' }}
                                                        />
                                                    </TableCell>
                                                </>
                                            )}
                                            {isAdmin && (
                                                <TableCell align="left" sx={{ py: 1.5 }}>
                                                    <Box sx={{ borderLeft: '3px solid #b0b0b0ff', pl: 1.5, textAlign: 'left', minWidth: '100px' }}>
                                                        <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                                            {log.userName}
                                                        </Typography>
                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                            {issueTypeLabels[log.issueType] || "-"}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            )}
                                            <TableCell align="center" sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
                                                {formatTime(log.startTime)}
                                            </TableCell>
                                            <TableCell align="center" sx={{ py: 1.5, whiteSpace: 'nowrap' }}>{formatTime(log.endTime)}</TableCell>
                                            <TableCell align="right" sx={{ py: 1.5 }}>{log.duration}</TableCell>
                                        </TableRow>
                                    ))
                                ))}
                                {taskDetails.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                            No tasks found for this date.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                            {taskDetails.length > 0 && (
                                <TableRow sx={{ bgcolor: '#f0faff' }}>
                                    <TableCell colSpan={isAdmin ? 6 : 5} align="right" sx={{ fontWeight: 'bold' }}>
                                        Total Hours:
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {totalHours}
                                    </TableCell>
                                </TableRow>
                            )}
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ClientTaskDetailsDialog;
